import React, { createContext, useCallback, useContext, useEffect, useRef } from "react";
import { useMarkManyRead } from "../../../hooks/useNotifications";

/**
 * Collects notifications that have been seen and marks them read in batches.
 *
 * Rows report themselves individually as they scroll into view, but opening a
 * list can reveal several at once and scrolling reveals many more. One request
 * per row would spend a user's `communityWrite` budget — 30/minute, shared with
 * liking, commenting and publishing — on nothing but reading their own
 * notifications. So ids are queued and flushed together on a short debounce.
 *
 * `markSeen` is a no-op outside a provider, so NotificationRow stays usable
 * anywhere without the caller having to know about batching.
 */
const SeenContext = createContext(() => {});

const FLUSH_DELAY_MS = 800;
/** Matches MAX_MARK_READ_IDS on the server. */
const MAX_BATCH = 100;

export const useMarkSeen = () => useContext(SeenContext);

export const SeenBatcherProvider = ({ children }) => {
  const { mutate: markManyRead } = useMarkManyRead();
  const queueRef = useRef(new Set());
  const timerRef = useRef(null);
  // Ids already sent, so a row re-entering the viewport does not re-queue one
  // the server has already been told about.
  const sentRef = useRef(new Set());

  const flush = useCallback(() => {
    timerRef.current = null;
    const ids = [...queueRef.current].slice(0, MAX_BATCH);
    if (ids.length === 0) return;
    ids.forEach((id) => {
      queueRef.current.delete(id);
      sentRef.current.add(id);
    });
    markManyRead(ids);
    // Anything over the cap stays queued for the next flush.
    if (queueRef.current.size > 0) {
      timerRef.current = setTimeout(flush, FLUSH_DELAY_MS);
    }
  }, [markManyRead]);

  const markSeen = useCallback(
    (id) => {
      if (!id || sentRef.current.has(id) || queueRef.current.has(id)) return;
      queueRef.current.add(id);
      if (timerRef.current === null) {
        timerRef.current = setTimeout(flush, FLUSH_DELAY_MS);
      }
    },
    [flush]
  );

  // Closing the dropdown unmounts the provider mid-debounce, so send whatever
  // is still queued rather than dropping it — otherwise a notification the user
  // demonstrably looked at stays unread because they closed the panel quickly.
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      const ids = [...queueRef.current].slice(0, MAX_BATCH);
      if (ids.length > 0) markManyRead(ids);
    };
  }, [markManyRead]);

  return <SeenContext.Provider value={markSeen}>{children}</SeenContext.Provider>;
};
